import { Link, useParams } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { Transaction } from "@/apis/gql/types";
import { BankReauthAlert } from "@/components/shared/alerts/bank-reauth-alert";
import { TransactionsBadge } from "@/components/shared/app-navigation/sidebar/badges/company";
import AIProcessingCard from "@/components/shared/cards/ai-processing-card";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
} from "@/components/ui/navigation-menu";
import { AllTransactions } from "@/features/transactions/shared/all-transactions";
import UploadProgressBox from "@/features/upload-progress-box/UploadProgressBox";
import { useAddInvoiceToTransaction } from "@/queries/transactions/add-invoice";
import { useDeleteInvoiceFromTransaction } from "@/queries/transactions/delete-invoice";

import { useAllTransactionsTable } from "../shared/use-all-transactions-table";

import { useArrayQueryState } from "@/hooks/useArrayQueryState";
import { cn } from "@/lib/utils";
import { TransactionsTableFilters } from "../shared/transactions-table-filters";
import { columns } from "./all-transactions-columns";
import { InvoiceMatchingPanel } from "./invoice-matching-panel";

export enum TransactionsTabsValues {
  TODOS = "transactions-todos",
  ALL_TRANSACTIONS = "all-transactions",
}

export default function Transactions() {
  const { t } = useTranslation();
  const { company: companyId } = useParams({
    from: "/_authenticated/$company",
  });
  const [addInvoiceToTransaction] = useAddInvoiceToTransaction();
  const [deleteInvoiceFromTransaction] = useDeleteInvoiceFromTransaction();

  const [selectedTransactionIds, setSelectedTransactionIds] =
    useArrayQueryState("selected-transaction-ids");

  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<
    Record<string, boolean>
  >({});

  const allTransactions = useAllTransactionsTable({
    columns,
    rowSelection: selectedTransactionIds,
    onRowSelectionChange: setSelectedTransactionIds,
  });

  const { globalSearch, setGlobalSearch, filters, setFilters } =
    allTransactions;

  const transactionsForPanel = allTransactions.table
    .getFilteredSelectedRowModel()
    .rows.map((row) => row.original) as Transaction[];

  const isPanelOpen = transactionsForPanel.length > 0;

  useEffect(() => {
    // Small delay to let the DOM update, then trigger resize
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, 100);
    return () => clearTimeout(timer);
  }, [isPanelOpen]);

  return (
    <>
      <div className="h-full flex">
        <div className="flex flex-1">
          <div className="flex-1 flex flex-col min-w-0">
            <div className="flex flex-col px-4 gap-2 bg-background">
              <div className="flex gap-4 overflow-x-auto whitespace-nowrap">
                <BankReauthAlert className="pb-4 max-w-md h-full flex-shrink-0" />
                <AIProcessingCard screen="txn-todos" className="mb-4" />
              </div>
            </div>
            <div className="flex-1 flex">
              <div className="flex flex-col min-w-0 w-full relative">
                <div>
                  <NavigationMenu className="px-4 bg-background max-w-full justify-start border-b">
                    <NavigationMenuList className="space-x-0">
                      <NavigationMenuItem>
                        <Link
                          to="/$company/unternehmen/transaktionen/todos"
                          params={{ company: companyId }}
                        >
                          <NavigationMenuLink
                            active
                            className={cn(
                              "flex items-center pb-3 px-6 gap-4 font-medium text-lg",
                              "inline-flex justify-center whitespace-nowrap ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
                              "text-gray-500",
                            )}
                          >
                            {t("transactions.tabs.todos")}
                            <TransactionsBadge />
                          </NavigationMenuLink>
                        </Link>
                      </NavigationMenuItem>
                      <NavigationMenuItem>
                        <Link
                          to="/$company/unternehmen/transaktionen"
                          params={{ company: companyId }}
                        >
                          <NavigationMenuLink
                            active
                            className={cn(
                              "flex items-center pb-3 px-6 gap-2 font-medium text-lg rounded-none",
                              "inline-flex justify-center whitespace-nowrap ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
                              "border-b-[3px] text-primary border-primary cursor-default",
                            )}
                            onClick={(e) => e.preventDefault()}
                          >
                            {t("transactions.tabs.all_transactions")}
                          </NavigationMenuLink>
                        </Link>
                      </NavigationMenuItem>
                    </NavigationMenuList>
                  </NavigationMenu>
                </div>
                <div className="flex-1 flex flex-col">
                  <div className="flex-1 px-4 pb-4">
                    <AllTransactions
                      {...allTransactions}
                      selectedInvoicesIds={selectedInvoiceIds}
                      handleMatch={handleMatch}
                    >
                      <AllTransactions.Filters>
                        <TransactionsTableFilters
                          globalSearch={globalSearch}
                          setGlobalSearch={setGlobalSearch}
                          filters={filters}
                          setFilters={setFilters}
                        />
                      </AllTransactions.Filters>
                    </AllTransactions>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <AnimatePresence mode="wait">
            {transactionsForPanel.length > 0 && (
              <motion.div
                key="invoice-matching-panel-wrapper"
                className="bg-background z-10 overflow-hidden"
                style={{
                  width: "50%",
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{
                  duration: 0.3,
                  ease: [0.4, 0, 0.2, 1],
                }}
              >
                <InvoiceMatchingPanel
                  key={transactionsForPanel.map((t) => t.bankTxnId).join(",")}
                  transactions={transactionsForPanel}
                  onRowSelectionChange={setSelectedInvoiceIds}
                  rowSelection={selectedInvoiceIds}
                  onMatch={handleMatch}
                  onUnmatch={handleUnmatch}
                  onClose={handleClosePanel}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      <UploadProgressBox />
    </>
  );

  async function handleMatch(invoiceIds?: string[]) {
    const relevantInvoiceIds = invoiceIds ?? Object.keys(selectedInvoiceIds);

    const bankTxnIds = transactionsForPanel.map((t) => t.bankTxnId);

    await addInvoiceToTransaction({
      bankTxnIds,
      invoiceIds: relevantInvoiceIds,
    });

    setSelectedInvoiceIds({});
  }

  async function handleUnmatch(invoiceIds?: string[]) {
    const relevantInvoiceIds = invoiceIds ?? Object.keys(selectedInvoiceIds);

    const bankTxnIds = transactionsForPanel.map((t) => t.bankTxnId);

    await deleteInvoiceFromTransaction({
      bankTxnIds,
      invoiceIds: relevantInvoiceIds,
    });
  }

  function handleClosePanel() {
    setSelectedTransactionIds({});
    setSelectedInvoiceIds({});
  }
}
